import { useCallback } from 'react';
import isSameDay from 'date-fns/isSameDay';
import isNumber from 'lodash/isNumber';
import isNil from 'lodash/isNil';

import {
  UniversalFilterDateValue,
  UniversalFilterDateModes,
  UniversalFilterDateFilterState,
  UniversalFilterDateOnChange
} from '../../UniversalFilterDate.types';

import { UniversalFilterDateIsAfterModeOptions } from '../../components/UniversalFilterDateIsAfterMode';
import { UniversalFilterDateIsBeforeModeOptions } from '../../components/UniversalFilterDateIsBeforeMode';
import { UniversalFilterDateIsModeOptions } from '../../components/UniversalFilterDateIsMode';
import { UniversalFilterDateIsOnOrAfterModeOptions } from '../../components/UniversalFilterDateIsOnOrAfterMode';
import { UniversalFilterDateIsOnOrBeforeModeOptions } from '../../components/UniversalFilterDateIsOnOrBeforeMode';
import { UniversalFilterDateIsWithinModeOptions } from '../../components/UniversalFilterDateIsWithinMode';

import { UniversalFilterDateUtils } from '../../utils/UniversalFilterDateUtils';

const getValueByModeAndOption = {
  [UniversalFilterDateModes.IS]: {
    [UniversalFilterDateIsModeOptions.EXACT_DAY]: (
      filterState: UniversalFilterDateFilterState
    ) =>
      filterState?.optionDate
        ? UniversalFilterDateUtils.getDayRangeByDate(filterState.optionDate)
        : null,
    [UniversalFilterDateIsModeOptions.TODAY]: () =>
      UniversalFilterDateUtils.getDayRangeWithDaysOffset(),
    [UniversalFilterDateIsModeOptions.TOMORROW]: () =>
      UniversalFilterDateUtils.getDayRangeWithDaysOffset(1),
    [UniversalFilterDateIsModeOptions.YESTERDAY]: () =>
      UniversalFilterDateUtils.getDayRangeWithDaysOffset(-1),
    [UniversalFilterDateIsModeOptions.ONE_WEEK_AGO]: () =>
      UniversalFilterDateUtils.getDayRangeWithDaysOffset(-7),
    [UniversalFilterDateIsModeOptions.ONE_WEEK_FROM_NOW]: () =>
      UniversalFilterDateUtils.getDayRangeWithDaysOffset(7),
    [UniversalFilterDateIsModeOptions.ONE_MONTH_AGO]: () =>
      UniversalFilterDateUtils.getDayRangeWithMonthsOffset(-1),
    [UniversalFilterDateIsModeOptions.ONE_MONTH_FROM_NOW]: () =>
      UniversalFilterDateUtils.getDayRangeWithMonthsOffset(1),
    [UniversalFilterDateIsModeOptions.NUMBER_OF_DAYS_AGO]: (
      filterState: UniversalFilterDateFilterState
    ) =>
      isNumber(filterState?.optionDays)
        ? UniversalFilterDateUtils.getDayRangeWithDaysOffset(
            -filterState.optionDays
          )
        : undefined,
    [UniversalFilterDateIsModeOptions.NUMBER_OF_DAYS_FROM_NOW]: (
      filterState: UniversalFilterDateFilterState
    ) =>
      isNumber(filterState?.optionDays)
        ? UniversalFilterDateUtils.getDayRangeWithDaysOffset(
            filterState.optionDays
          )
        : undefined
  },
  [UniversalFilterDateModes.IS_WITHIN]: {
    [UniversalFilterDateIsWithinModeOptions.THE_PAST_WEEK]: () =>
      UniversalFilterDateUtils.getRangeFromTodayByDaysOffset(-6),
    [UniversalFilterDateIsWithinModeOptions.THE_PAST_MONTH]: () =>
      UniversalFilterDateUtils.getRangeFromTodayByMonthsOffset(-1),
    [UniversalFilterDateIsWithinModeOptions.THE_PAST_YEAR]: () =>
      UniversalFilterDateUtils.getRangeFromTodayByYearsOffset(-1),
    [UniversalFilterDateIsWithinModeOptions.THE_NEXT_WEEK]: () =>
      UniversalFilterDateUtils.getRangeFromTodayByDaysOffset(6),
    [UniversalFilterDateIsWithinModeOptions.THE_NEXT_MONTH]: () =>
      UniversalFilterDateUtils.getRangeFromTodayByMonthsOffset(1),
    [UniversalFilterDateIsWithinModeOptions.THE_NEXT_YEAR]: () =>
      UniversalFilterDateUtils.getRangeFromTodayByYearsOffset(1),
    [UniversalFilterDateIsWithinModeOptions.THIS_CALENDAR_WEEK]: () =>
      UniversalFilterDateUtils.getCalendarWeekRange(),
    [UniversalFilterDateIsWithinModeOptions.THIS_CALENDAR_MONTH]: () =>
      UniversalFilterDateUtils.getCalendarMonthRange(),
    [UniversalFilterDateIsWithinModeOptions.THIS_CALENDAR_YEAR]: () =>
      UniversalFilterDateUtils.getCalendarYearRange(),
    [UniversalFilterDateIsWithinModeOptions.THE_NEXT_NUMBER_OF_DAYS]: (
      filterState: UniversalFilterDateFilterState
    ) =>
      isNumber(filterState?.optionDays)
        ? UniversalFilterDateUtils.getRangeFromTodayByDaysOffset(
            filterState.optionDays
          )
        : undefined,
    [UniversalFilterDateIsWithinModeOptions.THE_PAST_NUMBER_OF_DAYS]: (
      filterState: UniversalFilterDateFilterState
    ) =>
      isNumber(filterState?.optionDays)
        ? UniversalFilterDateUtils.getRangeFromTodayByDaysOffset(
            -filterState.optionDays
          )
        : undefined
  },
  [UniversalFilterDateModes.IS_BEFORE]: {
    [UniversalFilterDateIsBeforeModeOptions.EXACT_DAY]: (
      filterState: UniversalFilterDateFilterState
    ) =>
      filterState?.optionDate
        ? UniversalFilterDateUtils.getBeforeByDate(filterState.optionDate)
        : null,
    [UniversalFilterDateIsBeforeModeOptions.TODAY]: () =>
      UniversalFilterDateUtils.getBeforeWithDaysOffset(),
    [UniversalFilterDateIsBeforeModeOptions.TOMORROW]: () =>
      UniversalFilterDateUtils.getBeforeWithDaysOffset(1),
    [UniversalFilterDateIsBeforeModeOptions.YESTERDAY]: () =>
      UniversalFilterDateUtils.getBeforeWithDaysOffset(-1),
    [UniversalFilterDateIsBeforeModeOptions.ONE_WEEK_AGO]: () =>
      UniversalFilterDateUtils.getBeforeWithDaysOffset(-7),
    [UniversalFilterDateIsBeforeModeOptions.ONE_WEEK_FROM_NOW]: () =>
      UniversalFilterDateUtils.getBeforeWithDaysOffset(7),
    [UniversalFilterDateIsBeforeModeOptions.ONE_MONTH_AGO]: () =>
      UniversalFilterDateUtils.getBeforeWithMonthsOffset(-1),
    [UniversalFilterDateIsBeforeModeOptions.ONE_MONTH_FROM_NOW]: () =>
      UniversalFilterDateUtils.getBeforeWithMonthsOffset(1),
    [UniversalFilterDateIsBeforeModeOptions.NUMBER_OF_DAYS_AGO]: (
      filterState: UniversalFilterDateFilterState
    ) =>
      isNumber(filterState?.optionDays)
        ? UniversalFilterDateUtils.getBeforeWithDaysOffset(
            -filterState.optionDays
          )
        : undefined,
    [UniversalFilterDateIsBeforeModeOptions.NUMBER_OF_DAYS_FROM_NOW]: (
      filterState: UniversalFilterDateFilterState
    ) =>
      isNumber(filterState?.optionDays)
        ? UniversalFilterDateUtils.getBeforeWithDaysOffset(
            filterState.optionDays
          )
        : undefined
  },
  [UniversalFilterDateModes.IS_AFTER]: {
    [UniversalFilterDateIsAfterModeOptions.EXACT_DAY]: (
      filterState: UniversalFilterDateFilterState
    ) =>
      filterState?.optionDate
        ? UniversalFilterDateUtils.getAfterByDate(filterState.optionDate)
        : null,
    [UniversalFilterDateIsAfterModeOptions.TODAY]: () =>
      UniversalFilterDateUtils.getAfterWithDaysOffset(),
    [UniversalFilterDateIsAfterModeOptions.TOMORROW]: () =>
      UniversalFilterDateUtils.getAfterWithDaysOffset(1),
    [UniversalFilterDateIsAfterModeOptions.YESTERDAY]: () =>
      UniversalFilterDateUtils.getAfterWithDaysOffset(-1),
    [UniversalFilterDateIsAfterModeOptions.ONE_WEEK_AGO]: () =>
      UniversalFilterDateUtils.getAfterWithDaysOffset(-7),
    [UniversalFilterDateIsAfterModeOptions.ONE_WEEK_FROM_NOW]: () =>
      UniversalFilterDateUtils.getAfterWithDaysOffset(7),
    [UniversalFilterDateIsAfterModeOptions.ONE_MONTH_AGO]: () =>
      UniversalFilterDateUtils.getAfterWithMonthsOffset(-1),
    [UniversalFilterDateIsAfterModeOptions.ONE_MONTH_FROM_NOW]: () =>
      UniversalFilterDateUtils.getAfterWithMonthsOffset(1),
    [UniversalFilterDateIsAfterModeOptions.NUMBER_OF_DAYS_AGO]: (
      filterState: UniversalFilterDateFilterState
    ) =>
      isNumber(filterState?.optionDays)
        ? UniversalFilterDateUtils.getAfterWithDaysOffset(
            -filterState.optionDays
          )
        : undefined,
    [UniversalFilterDateIsAfterModeOptions.NUMBER_OF_DAYS_FROM_NOW]: (
      filterState: UniversalFilterDateFilterState
    ) =>
      isNumber(filterState?.optionDays)
        ? UniversalFilterDateUtils.getAfterWithDaysOffset(
            filterState.optionDays
          )
        : undefined
  },
  [UniversalFilterDateModes.IS_ON_OR_BEFORE]: {
    [UniversalFilterDateIsOnOrBeforeModeOptions.EXACT_DAY]: (
      filterState: UniversalFilterDateFilterState
    ) =>
      filterState?.optionDate
        ? UniversalFilterDateUtils.getOnOrBeforeByDate(filterState.optionDate)
        : null,
    [UniversalFilterDateIsOnOrBeforeModeOptions.TODAY]: () =>
      UniversalFilterDateUtils.getOnOrBeforeWithDaysOffset(),
    [UniversalFilterDateIsOnOrBeforeModeOptions.TOMORROW]: () =>
      UniversalFilterDateUtils.getOnOrBeforeWithDaysOffset(1),
    [UniversalFilterDateIsOnOrBeforeModeOptions.YESTERDAY]: () =>
      UniversalFilterDateUtils.getOnOrBeforeWithDaysOffset(-1),
    [UniversalFilterDateIsOnOrBeforeModeOptions.ONE_WEEK_AGO]: () =>
      UniversalFilterDateUtils.getOnOrBeforeWithDaysOffset(-7),
    [UniversalFilterDateIsOnOrBeforeModeOptions.ONE_WEEK_FROM_NOW]: () =>
      UniversalFilterDateUtils.getOnOrBeforeWithDaysOffset(7),
    [UniversalFilterDateIsOnOrBeforeModeOptions.ONE_MONTH_AGO]: () =>
      UniversalFilterDateUtils.getOnOrBeforeWithMonthsOffset(-1),
    [UniversalFilterDateIsOnOrBeforeModeOptions.ONE_MONTH_FROM_NOW]: () =>
      UniversalFilterDateUtils.getOnOrBeforeWithMonthsOffset(1),
    [UniversalFilterDateIsOnOrBeforeModeOptions.NUMBER_OF_DAYS_AGO]: (
      filterState: UniversalFilterDateFilterState
    ) =>
      isNumber(filterState?.optionDays)
        ? UniversalFilterDateUtils.getOnOrBeforeWithDaysOffset(
            -filterState.optionDays
          )
        : undefined,
    [UniversalFilterDateIsOnOrBeforeModeOptions.NUMBER_OF_DAYS_FROM_NOW]: (
      filterState: UniversalFilterDateFilterState
    ) =>
      isNumber(filterState?.optionDays)
        ? UniversalFilterDateUtils.getOnOrBeforeWithDaysOffset(
            filterState.optionDays
          )
        : undefined
  },
  [UniversalFilterDateModes.IS_ON_OR_AFTER]: {
    [UniversalFilterDateIsOnOrAfterModeOptions.EXACT_DAY]: (
      filterState: UniversalFilterDateFilterState
    ) =>
      filterState?.optionDate
        ? UniversalFilterDateUtils.getOnOrAfterByDate(filterState.optionDate)
        : null,
    [UniversalFilterDateIsOnOrAfterModeOptions.TODAY]: () =>
      UniversalFilterDateUtils.getOnOrAfterWithDaysOffset(),
    [UniversalFilterDateIsOnOrAfterModeOptions.TOMORROW]: () =>
      UniversalFilterDateUtils.getOnOrAfterWithDaysOffset(1),
    [UniversalFilterDateIsOnOrAfterModeOptions.YESTERDAY]: () =>
      UniversalFilterDateUtils.getOnOrAfterWithDaysOffset(-1),
    [UniversalFilterDateIsOnOrAfterModeOptions.ONE_WEEK_AGO]: () =>
      UniversalFilterDateUtils.getOnOrAfterWithDaysOffset(-7),
    [UniversalFilterDateIsOnOrAfterModeOptions.ONE_WEEK_FROM_NOW]: () =>
      UniversalFilterDateUtils.getOnOrAfterWithDaysOffset(7),
    [UniversalFilterDateIsOnOrAfterModeOptions.ONE_MONTH_AGO]: () =>
      UniversalFilterDateUtils.getOnOrAfterWithMonthsOffset(-1),
    [UniversalFilterDateIsOnOrAfterModeOptions.ONE_MONTH_FROM_NOW]: () =>
      UniversalFilterDateUtils.getOnOrAfterWithMonthsOffset(1),
    [UniversalFilterDateIsOnOrAfterModeOptions.NUMBER_OF_DAYS_AGO]: (
      filterState: UniversalFilterDateFilterState
    ) =>
      isNumber(filterState?.optionDays)
        ? UniversalFilterDateUtils.getOnOrAfterWithDaysOffset(
            -filterState.optionDays
          )
        : undefined,
    [UniversalFilterDateIsOnOrAfterModeOptions.NUMBER_OF_DAYS_FROM_NOW]: (
      filterState: UniversalFilterDateFilterState
    ) =>
      isNumber(filterState?.optionDays)
        ? UniversalFilterDateUtils.getOnOrAfterWithDaysOffset(
            filterState.optionDays
          )
        : undefined
  }
};

interface UniversalFilterDateFilterStateOptions {
  onChange: UniversalFilterDateOnChange;
}

function useUniversalFilterDateFilterState({
  onChange
}: UniversalFilterDateFilterStateOptions) {
  // All modes and options available in handleApplyFilterState
  // must be implemented in handleParseFilterValue too
  const handleApplyFilterState = useCallback<
    (filterState: UniversalFilterDateFilterState) => void
  >(
    (filterState) => {
      if (filterState?.mode === UniversalFilterDateModes.IS_NOT_EMPTY) {
        onChange({ isNotNull: true });
        return;
      }
      if (filterState?.mode === UniversalFilterDateModes.IS_EMPTY) {
        onChange({ isNull: true });
        return;
      }

      const getValue =
        getValueByModeAndOption[filterState?.mode]?.[filterState?.option];

      const value = getValue?.(filterState) || undefined;

      onChange(value);
    },
    [onChange]
  );

  const handleParseFilterValue = useCallback<
    (
      filterValue: UniversalFilterDateValue
    ) => UniversalFilterDateFilterState | undefined
  >((filterValue) => {
    if (isNil(filterValue)) {
      return undefined;
    }

    if (filterValue.isNotNull === true) {
      return {
        mode: UniversalFilterDateModes.IS_NOT_EMPTY
      };
    }

    if (filterValue.isNull === true) {
      return {
        mode: UniversalFilterDateModes.IS_EMPTY
      };
    }

    if (
      filterValue.gte &&
      filterValue.lte &&
      isSameDay(new Date(filterValue.gte), new Date(filterValue.lte))
    ) {
      return {
        mode: UniversalFilterDateModes.IS,
        option: UniversalFilterDateIsModeOptions.EXACT_DAY,
        optionDate: filterValue.gte
      };
    }

    if (filterValue.gte && !filterValue.lte) {
      return {
        mode: UniversalFilterDateModes.IS_ON_OR_AFTER,
        option: UniversalFilterDateIsOnOrAfterModeOptions.EXACT_DAY,
        optionDate: filterValue.gte
      };
    }

    if (!filterValue.gte && filterValue.lte) {
      return {
        mode: UniversalFilterDateModes.IS_ON_OR_BEFORE,
        option: UniversalFilterDateIsOnOrBeforeModeOptions.EXACT_DAY,
        optionDate: filterValue.lte
      };
    }

    return undefined;
  }, []);

  return { handleApplyFilterState, handleParseFilterValue };
}

export default useUniversalFilterDateFilterState;
